import {
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
} from "solid-js";
import { api } from "../api/client";
import type { CatalogExercise } from "../api/types";
import { t, type MessageKey } from "../i18n";
import { typeLabel, type ExerciseType } from "../lib/activity";
import { useCatalog } from "../lib/data";
import CatalogExerciseSheet from "./CatalogExerciseSheet";
import ExerciseIcon from "./ExerciseIcon";

interface Props {
  value: string;
  placeholder?: string;
  class?: string;
  /** Icon/type of the exercise currently in the row (shown inside the field). */
  icon?: string | null;
  type?: ExerciseType;
  onChange: (name: string) => void;
  /** Existing catalog pick or silent resolve of a free-typed name. */
  onPick?: (item: CatalogExercise) => void;
  onBlurName?: (name: string) => void;
}

export function rankCatalog(
  items: CatalogExercise[],
  query: string,
  limit = 12,
): CatalogExercise[] {
  const q = query.trim().toLowerCase();
  if (!q) return items.slice(0, limit);
  const starts: CatalogExercise[] = [];
  const includes: CatalogExercise[] = [];
  for (const item of items) {
    const name = item.name.toLowerCase();
    if (name.startsWith(q)) starts.push(item);
    else if (name.includes(q)) includes.push(item);
  }
  return [...starts, ...includes].slice(0, limit);
}

/**
 * Exercise name field with catalog typeahead.
 * New names are ensured into the catalog automatically on blur (no extra UI).
 * Backend also ensures on workout save as a safety net.
 */
export default function ExerciseNameLookup(props: Props) {
  const [open, setOpen] = createSignal(false);
  // One shared catalog for every row (loaded once, cached on the device).
  const [catalog, { mutate }] = useCatalog();
  const [ensuring, setEnsuring] = createSignal(false);
  const [sheetOpen, setSheetOpen] = createSignal(false);
  const [sheetName, setSheetName] = createSignal("");

  let rootEl: HTMLDivElement | undefined;
  let blurTimer: ReturnType<typeof setTimeout> | undefined;
  /** Skip ensure when blur follows a dropdown pick. */
  let pickedFromList = false;

  const query = createMemo(() => props.value.trim());
  const suggestions = createMemo(() =>
    rankCatalog(catalog() ?? [], props.value, 60),
  );

  const exactMatch = createMemo(() => {
    const q = query().toLowerCase();
    if (!q) return null;
    return (
      (catalog() ?? []).find((item) => item.name.trim().toLowerCase() === q) ??
      null
    );
  });

  const isNewName = createMemo(
    () => query().length > 0 && !exactMatch() && !catalog.loading,
  );

  createEffect(() => {
    if (!open()) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootEl?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDoc);
    onCleanup(() => document.removeEventListener("pointerdown", onDoc));
  });

  onCleanup(() => {
    if (blurTimer) clearTimeout(blurTimer);
  });

  function applyCatalogItem(item: CatalogExercise) {
    props.onChange(item.name);
    props.onPick?.(item);
    const prev = catalog() ?? [];
    if (!prev.some((x) => x.id === item.id)) {
      mutate([item, ...prev]);
    }
  }

  function pick(item: CatalogExercise) {
    pickedFromList = true;
    applyCatalogItem(item);
    setOpen(false);
  }

  function openCustom() {
    setSheetName(props.value.trim());
    setSheetOpen(true);
    setOpen(false);
  }

  function savedCustom(item: CatalogExercise) {
    setSheetOpen(false);
    pickedFromList = true;
    applyCatalogItem(item);
    props.onBlurName?.(item.name);
    pickedFromList = false;
  }

  /** Get-or-create catalog entry for free-typed names (silent). */
  async function ensureFromName(raw: string) {
    const name = raw.trim();
    if (!name || ensuring()) return;
    const local = (catalog() ?? []).find(
      (item) => item.name.trim().toLowerCase() === name.toLowerCase(),
    );
    if (local) {
      applyCatalogItem(local);
      return;
    }
    setEnsuring(true);
    try {
      const result = await api.ensureCatalogExercise({ name });
      applyCatalogItem(result);
    } catch {
      // Backend will still ensure on workout save
    } finally {
      setEnsuring(false);
    }
  }

  return (
    <div
      ref={rootEl}
      class={`exercise-name-lookup relative min-w-0 flex-1 ${
        open() ? "is-lookup-open z-50" : ""
      } ${props.class ?? ""}`}
    >
      <div class="name-with-icon">
        <ExerciseIcon icon={props.icon} type={props.type} />
        <input
        class="input w-full font-semibold"
        value={props.value}
        placeholder={props.placeholder ?? t("EXERCISE")}
        autocomplete="off"
        role="combobox"
        aria-expanded={open()}
        aria-autocomplete="list"
        onFocus={() => setOpen(true)}
        onInput={(e) => {
          props.onChange(e.currentTarget.value);
          setOpen(true);
        }}
        onBlur={() => {
          blurTimer = setTimeout(() => {
            setOpen(false);
            // Focus moved into the "new activity" sheet: the name is not final yet.
            if (sheetOpen()) return;
            const name = props.value.trim();
            if (name !== props.value) {
              props.onChange(name);
            }
            if (pickedFromList) {
              pickedFromList = false;
              props.onBlurName?.(name);
              return;
            }
            if (name) {
              void ensureFromName(name).then(() => props.onBlurName?.(name));
            } else {
              props.onBlurName?.(name);
            }
          }, 150);
        }}
        />
      </div>

      <Show when={open()}>
        <div class="exercise-lookup absolute left-0 right-0 top-full z-[80] mt-1 max-h-56 overflow-y-auto overscroll-contain rounded-xl border border-surface-border bg-surface-raised p-1 shadow-card">
          <Show when={catalog.loading}>
            <p class="px-2 py-2 text-xs text-ink-muted">{t("LOADING")}</p>
          </Show>
          <Show
            when={
              !catalog.loading &&
              (catalog()?.length ?? 0) === 0 &&
              !isNewName()
            }
          >
            <p class="px-2 py-2 text-xs text-ink-muted">
              {t("NO_CATALOG_EXERCISES")}
            </p>
          </Show>
          <Show
            when={
              !catalog.loading &&
              (catalog()?.length ?? 0) > 0 &&
              suggestions().length === 0 &&
              !isNewName()
            }
          >
            <p class="px-2 py-2 text-xs text-ink-muted">{t("NO_DATA")}</p>
          </Show>
          <ul role="listbox">
            <For each={suggestions()}>
              {(item) => (
                <li role="option">
                  <button
                    type="button"
                    class="w-full rounded-lg px-2.5 py-2 text-left transition hover:bg-white/5 active:bg-accent/15"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(item)}
                  >
                    <div class="flex items-center gap-2 text-sm font-medium text-ink">
                      <ExerciseIcon
                        icon={item.icon}
                        type={item.exerciseType}
                        class="text-[var(--accent)]"
                      />
                      <span>{item.name}</span>
                    </div>
                    <div class="mt-0.5 text-xs text-ink-muted">
                      {[
                        typeLabel(item.exerciseType),
                        ...(item.muscleGroups ?? []).map((g) =>
                          t(`MG_${g.toUpperCase()}` as MessageKey),
                        ),
                      ].join(" · ")}
                    </div>
                  </button>
                </li>
              )}
            </For>
          </ul>
          <button
            type="button"
            class="w-full rounded-lg px-2.5 py-2 text-left text-sm text-[var(--link)] transition hover:bg-white/5"
            onMouseDown={(e) => e.preventDefault()}
            onClick={openCustom}
          >
            + {t("CREATE_CUSTOM_ACTIVITY")}
          </button>
          <Show when={isNewName()}>
            <p class="border-t border-surface-border px-2 py-1.5 text-[10px] text-ink-muted">
              {t("NEW_EXERCISE_AUTO_HINT")}
            </p>
          </Show>
          <Show when={!isNewName()}>
            <p class="border-t border-surface-border px-2 py-1.5 text-[10px] text-ink-muted">
              {t("CATALOG_LOOKUP_HINT")}
            </p>
          </Show>
        </div>
      </Show>
      <CatalogExerciseSheet
        open={sheetOpen()}
        initialName={sheetName()}
        onClose={() => setSheetOpen(false)}
        onSaved={savedCustom}
      />
    </div>
  );
}
